import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  Paper,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  BiotechOutlined as PathologyIcon,
  Delete as DeleteIcon,
  FavoriteBorder as HealthIcon,
  PersonOutlined as PersonalIcon,
} from '@mui/icons-material';
import {
  createImagingRow,
  createLabRow,
  createExamRow,
  createDifferentialRow,
  createId,
  fieldElementId,
  HEALTH_TEXT_FIELDS,
  HEALTH_OPTIONAL_FIELD_KEYS,
  alignCatalogRow,
  ANATOMAGE_PATIENT,
  evaluationToggleButtonSx,
  imagingCatalog,
  labCatalog,
  examCatalog,
  differentialCatalog,
  matchCatalogOption,
  mergeHealthTemplate,
  mergePathologyTemplate,
  mergeSavedHealthTemplate,
  mergeSavedPathologyTemplate,
  MARITAL_STATUS_OPTIONS,
  sanitizeDecimal,
  sanitizeInteger,
  SEX_OPTIONS,
  VITAL_FIELDS,
  VITAL_HINT,
} from '../../utils/myPatients';
import {
  buildFileOptions,
  fileOptionsWithSelection,
  resolveFileOption,
} from '../../utils/myFiles';
import { listMyFiles } from '../../utils/userContentApi';
import { translateLabResultToPlaintext } from '../../utils/labResults';
import { personalizeIntegratedTemplatePayload } from '../../utils/customPatientTemplateText';
import MetahumanCharacterPicker from './MetahumanCharacterPicker';
import PatientTemplatePicker from './PatientTemplatePicker';
import { FORM_SECTION_CARD_SX } from '../../constants/formSectionCardSx';
import { clientSx } from '../../theme';

const TABS = [
  { id: 'personal', label: 'Personal', icon: PersonalIcon },
  { id: 'health', label: 'Health', icon: HealthIcon },
  { id: 'pathology', label: 'Pathology', icon: PathologyIcon },
];

function Section({ title, description, children, sx: sectionSx }) {
  return (
    <Paper elevation={0} sx={{ ...FORM_SECTION_CARD_SX, ...sectionSx }}>
      <Typography variant="h6" component="h2" sx={{ mb: description ? 0.5 : 2 }}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
      ) : null}
      {children}
    </Paper>
  );
}

function EntryField({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  multiline = false,
  minRows = 2,
  required = false,
  type,
  inputMode,
  endAdornment,
  placeholder,
  select = false,
  children,
}) {
  const help = error || helperText;
  return (
    <TextField
      id={id}
      fullWidth
      select={select}
      label={label}
      required={required}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
      error={Boolean(error)}
      helperText={help || undefined}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      type={type}
      placeholder={placeholder}
      autoComplete="off"
      inputProps={{
        inputMode,
        'aria-invalid': error ? true : undefined,
      }}
      InputProps={endAdornment ? {
        endAdornment: (
          <InputAdornment position="end" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            {endAdornment}
          </InputAdornment>
        ),
      } : undefined}
      InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      FormHelperTextProps={error ? { role: 'alert' } : undefined}
    >
      {children}
    </TextField>
  );
}

function ChoiceField(props) {
  const { options, ...field } = props;
  return (
    <EntryField {...field} select>
      <MenuItem value="">Select</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </EntryField>
  );
}

function TabPanel({ id, labelledBy, hidden, children }) {
  return (
    <Box
      role="tabpanel"
      id={id}
      aria-labelledby={labelledBy}
      hidden={hidden}
      sx={{ pt: 3 }}
    >
      {hidden ? null : children}
    </Box>
  );
}

function shouldIgnoreRowExpandClick(target) {
  if (!target || typeof target.closest !== 'function') return false;
  return !!target.closest(
    'button, a, input, textarea, select, [role="combobox"], [role="listbox"], [role="option"], .MuiAutocomplete-popper, .MuiPopover-root, .MuiModal-root, .MuiPickersPopper-root',
  );
}

function DataTable({
  columns,
  rows,
  emptyLabel,
  onRemove,
  renderCell,
  minWidth = 760,
  expandableResult = false,
}) {
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    setExpandedIds((prev) => {
      const live = new Set(rows.map((row) => row.id));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const toggleExpanded = (rowId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  return (
    <TableContainer
      sx={{
        ...clientSx.tableContainer,
        overflowX: 'auto',
      }}
    >
      <Table size="small" sx={{ minWidth }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                scope="col"
                sx={{ minWidth: column.minWidth, whiteSpace: 'nowrap' }}
              >
                {column.label}
              </TableCell>
            ))}
            <TableCell scope="col" sx={{ width: 72 }} aria-label="Actions" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length + 1}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  {emptyLabel}
                </Typography>
              </TableCell>
            </TableRow>
          ) : rows.map((row, index) => {
            const expanded = expandableResult && expandedIds.has(row.id);
            const cellContext = { expanded, expandableResult };
            return (
              <TableRow
                key={row.id}
                hover
                aria-expanded={expandableResult ? expanded : undefined}
                onClick={(event) => {
                  if (!expandableResult) return;
                  if (shouldIgnoreRowExpandClick(event.target)) return;
                  toggleExpanded(row.id);
                }}
                sx={{
                  ...(expandableResult && {
                    cursor: 'pointer',
                    transition: 'background-color 0.22s ease',
                    bgcolor: expanded ? 'action.hover' : undefined,
                  }),
                  '& .row-delete-btn': {
                    opacity: 0,
                    transition: 'opacity 0.15s ease-in-out',
                  },
                  '&:hover .row-delete-btn, & .row-delete-btn:focus-visible': {
                    opacity: 1,
                  },
                }}
              >
                {columns.map((column) => (
                  <TableCell key={column.key} sx={{ verticalAlign: 'top', py: 1.25 }}>
                    {renderCell(row, column, index, cellContext)}
                  </TableCell>
                ))}
                <TableCell sx={{ verticalAlign: 'top', width: 72, py: 1.25 }}>
                  <IconButton
                    type="button"
                    className="row-delete-btn"
                    aria-label={`Remove row ${index + 1}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onRemove(row.id);
                    }}
                    sx={{
                      width: 44,
                      height: 44,
                      color: 'error.main',
                      '&:hover': {
                        bgcolor: 'rgba(211, 47, 47, 0.08)',
                        color: 'error.dark',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CellField({
  id,
  label,
  placeholder,
  value,
  onChange,
  type,
  expanded = false,
}) {
  const motionMs = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 0
    : 280;

  return (
    <TextField
      id={id}
      fullWidth
      size="small"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onClick={(event) => event.stopPropagation()}
      onFocus={(event) => event.stopPropagation()}
      type={type}
      multiline
      minRows={expanded ? 5 : 1}
      maxRows={expanded ? 12 : 1}
      autoComplete="off"
      placeholder={type === 'date' ? undefined : placeholder}
      inputProps={{ 'aria-label': label }}
      sx={{
        '& .MuiInputBase-root': {
          minHeight: 44,
          alignItems: 'flex-start',
          transition: motionMs ? `min-height ${motionMs}ms ease` : 'none',
        },
        '& textarea': {
          transition: motionMs ? `height ${motionMs}ms ease` : 'none',
        },
      }}
    />
  );
}

function optionLabel(option) {
  return option?.name || '';
}

function CatalogCell({ id, label, placeholder, options, name, objectId, onChange }) {
  const selected = matchCatalogOption(options, { name, objectId });
  return (
    <Autocomplete
      id={id}
      size="small"
      options={options}
      value={selected}
      onChange={(_event, next) => {
        if (next) onChange(next.name, String(next.id));
        else onChange('', '');
      }}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={placeholder}
          inputProps={{
            ...params.inputProps,
            'aria-label': label,
          }}
          sx={{ '& .MuiInputBase-root': { minHeight: 44 } }}
        />
      )}
    />
  );
}

function EvaluationCell({ id, label, value, onChange }) {
  const selected = value === 'Recomm' ? 'Recomm' : 'Critical';
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={selected}
      onChange={(_event, next) => {
        if (next) onChange(next);
      }}
      aria-label={label}
      sx={{
        width: '100%',
        '& .MuiToggleButtonGroup-grouped': {
          borderColor: 'divider',
        },
      }}
    >
      <ToggleButton value="Recomm" aria-label="Recommended evaluation" sx={evaluationToggleButtonSx('Recomm')}>
        Recommended
      </ToggleButton>
      <ToggleButton value="Critical" aria-label="Critical evaluation" sx={evaluationToggleButtonSx('Critical')}>
        Critical
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

function FileTagCell({ id, label, options, value, onChange }) {
  return (
    <Autocomplete
      id={id}
      size="small"
      options={options}
      groupBy={(option) => option.group}
      value={value}
      onChange={(_event, next) => onChange(next)}
      getOptionLabel={(option) => option?.tag || ''}
      filterOptions={(options, state) => {
        const query = state.inputValue.trim().toLowerCase();
        if (!query) return options;
        return options.filter((option) => `${option.tag} ${option.name}`.toLowerCase().includes(query));
      }}
      isOptionEqualToValue={(option, selected) => (
        option.source === selected.source && String(option.id) === String(selected.id)
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="Search file tags"
          inputProps={{
            ...params.inputProps,
            'aria-label': label,
          }}
          sx={{ '& .MuiInputBase-root': { minHeight: 44 } }}
        />
      )}
    />
  );
}

const PatientEditor = ({
  patient,
  tab,
  onTabChange,
  errors,
  onChange,
  onBlurField,
  onCommitEdit,
  onFormBlurCapture,
  formId,
  onSave,
}) => {
  const pendingCellFocus = useRef('');
  const patientRef = useRef(patient);
  patientRef.current = patient;
  const [imagingOptions, setImagingOptions] = useState([]);
  const [labOptions, setLabOptions] = useState([]);
  const [examOptions, setExamOptions] = useState([]);
  const [differentialOptions, setDifferentialOptions] = useState([]);
  const [anatomageFiles, setAnatomageFiles] = useState([]);
  const [catalogNote, setCatalogNote] = useState('');
  const [fileNote, setFileNote] = useState('');
  const [library, setLibrary] = useState({ folders: [], files: [] });
  const [templateNotice, setTemplateNotice] = useState('');

  useEffect(() => {
    setTemplateNotice('');
  }, [tab]);

  const mapTemplateRows = (rawRows, catalog, createRow) => (
    (Array.isArray(rawRows) ? rawRows : []).map((raw) => {
      const row = {
        ...createRow(),
        ...raw,
        id: createId(),
      };
      return alignCatalogRow(row, catalog);
    })
  );

  const mapTemplateLabRows = (rawRows, catalog) => (
    mapTemplateRows(
      (Array.isArray(rawRows) ? rawRows : []).map((raw) => ({
        ...raw,
        result: translateLabResultToPlaintext(raw?.name, raw?.result),
      })),
      catalog,
      createLabRow,
    )
  );

  const applyHealthTemplate = (payload) => {
    if (payload?.source === 'saved') {
      if (!payload.health) return;
      onCommitEdit?.();
      onChange({
        ...patient,
        health: mergeSavedHealthTemplate(payload.health),
      });
      onCommitEdit?.();
      setTemplateNotice(`Health profile replaced from "${payload.name || 'your template'}". Review and edit as needed.`);
      return;
    }
    if (!payload?.health) return;
    onCommitEdit?.();
    const adapted = personalizeIntegratedTemplatePayload(payload, patient.personal.sex);
    onChange({
      ...patient,
      health: mergeHealthTemplate(adapted.health),
    });
    onCommitEdit?.();
    setTemplateNotice(`Health profile replaced from ${payload.name || ANATOMAGE_PATIENT}. Review and edit as needed.`);
  };

  const applyPathologyTemplate = (payload) => {
    if (payload?.source === 'saved') {
      if (!payload.pathology) return;
      onCommitEdit?.();
      const source = payload.pathology;
      onChange({
        ...patient,
        pathology: mergeSavedPathologyTemplate(source, {
          imaging: mapTemplateRows(source.imaging, imagingOptions, createImagingRow),
          labs: mapTemplateLabRows(source.labs, labOptions),
          exams: mapTemplateRows(source.exams, examOptions, createExamRow),
          differentials: mapTemplateRows(source.differentials, differentialOptions, createDifferentialRow),
        }),
      });
      onCommitEdit?.();
      setTemplateNotice(`Pathology replaced from "${payload.name || 'your template'}". Review and edit as needed.`);
      return;
    }
    if (!payload?.pathology) return;
    onCommitEdit?.();
    const adapted = personalizeIntegratedTemplatePayload(payload, patient.personal.sex);
    const source = adapted.pathology;
    onChange({
      ...patient,
      pathology: mergePathologyTemplate(source, {
        imaging: mapTemplateRows(source.imaging, imagingOptions, createImagingRow),
        labs: mapTemplateLabRows(source.labs, labOptions),
        exams: mapTemplateRows(source.exams, examOptions, createExamRow),
        differentials: mapTemplateRows(source.differentials, differentialOptions, createDifferentialRow),
      }),
    });
    onCommitEdit?.();
    setTemplateNotice(`Pathology replaced from ${payload.name || ANATOMAGE_PATIENT}. Review and edit as needed.`);
  };

  useEffect(() => {
    let cancelled = false;
    listMyFiles()
      .then((next) => {
        if (!cancelled) setLibrary(next && typeof next === 'object' ? next : { folders: [], files: [] });
      })
      .catch(() => {
        if (!cancelled) setFileNote('Your files could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const token = window.localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.allSettled([
      axios.get('/api/actions/objects', { headers }),
      axios.get('/api/admin/file-tags', { headers }),
    ]).then(([objectsResult, filesResult]) => {
      if (cancelled) return;
      const objects = objectsResult.status === 'fulfilled' && Array.isArray(objectsResult.value.data)
        ? objectsResult.value.data
        : [];
      const files = filesResult.status === 'fulfilled' && Array.isArray(filesResult.value.data)
        ? filesResult.value.data
        : [];
      const imaging = imagingCatalog(objects);
      const labs = labCatalog(objects);
      const exams = examCatalog(objects);
      const differentials = differentialCatalog(objects);
      setImagingOptions(imaging);
      setLabOptions(labs);
      setExamOptions(exams);
      setDifferentialOptions(differentials);
      setAnatomageFiles(files);
      if (objectsResult.status === 'fulfilled') {
        const current = patientRef.current;
        const nextImaging = current.pathology.imaging.map((row) => alignCatalogRow(row, imaging));
        const nextLabs = current.pathology.labs.map((row) => alignCatalogRow(row, labs));
        const nextExams = (current.pathology.exams || []).map((row) => alignCatalogRow(row, exams));
        const nextDifferentials = (current.pathology.differentials || []).map((row) => alignCatalogRow(row, differentials));
        const imagingChanged = nextImaging.some((row, index) => row !== current.pathology.imaging[index]);
        const labsChanged = nextLabs.some((row, index) => row !== current.pathology.labs[index]);
        const examsChanged = nextExams.some((row, index) => row !== (current.pathology.exams || [])[index]);
        const differentialsChanged = nextDifferentials.some(
          (row, index) => row !== (current.pathology.differentials || [])[index]
        );
        if (imagingChanged || labsChanged || examsChanged || differentialsChanged) {
          onChange({
            ...current,
            pathology: {
              ...current.pathology,
              imaging: nextImaging,
              labs: nextLabs,
              exams: nextExams,
              differentials: nextDifferentials,
            },
          });
        }
      }
      setCatalogNote(objectsResult.status === 'rejected'
        ? 'The imaging, lab, exam, and diagnosis lists could not be loaded.'
        : '');
      setFileNote(filesResult.status === 'rejected'
        ? 'Anatomage file tags could not be loaded. Files from My Files are still available.'
        : '');
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const fileOptions = useMemo(
    () => buildFileOptions(anatomageFiles, library),
    [anatomageFiles, library]
  );

  useEffect(() => {
    if (!pendingCellFocus.current) return undefined;
    const id = pendingCellFocus.current;
    pendingCellFocus.current = '';
    document.getElementById(id)?.focus();
    return undefined;
  }, [patient.pathology.imaging, patient.pathology.labs, patient.pathology.exams, patient.pathology.differentials]);
  const commitEdit = () => {
    onCommitEdit?.();
  };

  const patchPersonal = (field, value) => {
    onChange({
      ...patient,
      personal: { ...patient.personal, [field]: value },
    }, `personal.${field}`);
  };

  const patchHealth = (field, value) => {
    onChange({
      ...patient,
      health: { ...patient.health, [field]: value },
    }, `health.${field}`);
  };

  const patchPathology = (field, value) => {
    onChange({
      ...patient,
      pathology: { ...patient.pathology, [field]: value },
    }, `pathology.${field}`);
  };

  const patchVital = (field, value) => {
    onChange({
      ...patient,
      pathology: {
        ...patient.pathology,
        vitals: { ...patient.pathology.vitals, [field]: value },
      },
    }, `pathology.vitals.${field}`);
  };

  const patchRows = (listName, rows) => {
    onChange({
      ...patient,
      pathology: { ...patient.pathology, [listName]: rows },
    });
  };

  const fieldProps = (section, field, value) => {
    const key = `${section}.${field}`;
    return {
      id: fieldElementId(patient.id, key),
      value,
      error: errors[key] || '',
      onBlur: () => {
        onBlurField(key);
      },
    };
  };

  const patchPersonalChoice = (field, value) => {
    commitEdit();
    patchPersonal(field, value);
    commitEdit();
  };

  const patchWithCommit = (patchFn) => (...args) => {
    commitEdit();
    patchFn(...args);
    commitEdit();
  };

  const patchImaging = (rowId, patch) => {
    patchRows('imaging', patient.pathology.imaging.map((item) => (
      item.id === rowId ? { ...item, ...patch } : item
    )));
  };

  const patchLab = (rowId, patch) => {
    patchRows('labs', patient.pathology.labs.map((item) => (
      item.id === rowId ? { ...item, ...patch } : item
    )));
  };

  const patchExam = (rowId, patch) => {
    patchRows('exams', (patient.pathology.exams || []).map((item) => (
      item.id === rowId ? { ...item, ...patch } : item
    )));
  };

  const syncPathologyNameFromDifferentials = (rows) => {
    const name = rows.map((row) => row.name.trim()).filter(Boolean).join(', ');
    onChange({
      ...patient,
      pathology: { ...patient.pathology, name, differentials: rows },
    });
  };

  const patchDifferentialCommitted = (rowId, patch) => {
    commitEdit();
    const nextRows = (patient.pathology.differentials || []).map((item) => (
      item.id === rowId ? { ...item, ...patch } : item
    ));
    syncPathologyNameFromDifferentials(nextRows);
    commitEdit();
  };

  const patchImagingCommitted = patchWithCommit(patchImaging);
  const patchLabCommitted = patchWithCommit(patchLab);
  const patchExamCommitted = patchWithCommit(patchExam);

  const addImaging = () => {
    commitEdit();
    const row = createImagingRow();
    pendingCellFocus.current = fieldElementId(patient.id, `imaging.${row.id}.name`);
    patchRows('imaging', [...patient.pathology.imaging, row]);
    commitEdit();
  };

  const addLab = () => {
    commitEdit();
    const row = createLabRow();
    pendingCellFocus.current = fieldElementId(patient.id, `lab.${row.id}.name`);
    patchRows('labs', [...patient.pathology.labs, row]);
    commitEdit();
  };

  const addExam = () => {
    commitEdit();
    const row = createExamRow();
    pendingCellFocus.current = fieldElementId(patient.id, `exam.${row.id}.name`);
    patchRows('exams', [...(patient.pathology.exams || []), row]);
    commitEdit();
  };

  const removeImagingRow = (rowId) => {
    commitEdit();
    patchRows('imaging', patient.pathology.imaging.filter((row) => row.id !== rowId));
    commitEdit();
  };

  const removeLabRow = (rowId) => {
    commitEdit();
    patchRows('labs', patient.pathology.labs.filter((row) => row.id !== rowId));
    commitEdit();
  };

  const removeExamRow = (rowId) => {
    commitEdit();
    patchRows('exams', (patient.pathology.exams || []).filter((row) => row.id !== rowId));
    commitEdit();
  };

  const removeDifferentialRow = (rowId) => {
    commitEdit();
    const nextRows = (patient.pathology.differentials || []).filter((row) => row.id !== rowId);
    syncPathologyNameFromDifferentials(nextRows);
    commitEdit();
  };

  const addDifferential = () => {
    commitEdit();
    const row = createDifferentialRow();
    pendingCellFocus.current = fieldElementId(patient.id, `differential.${row.id}.name`);
    patchRows('differentials', [...(patient.pathology.differentials || []), row]);
    commitEdit();
  };

  return (
    <Box
      id={formId}
      component="form"
      noValidate
      autoComplete="off"
      onBlurCapture={onFormBlurCapture}
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
    >
      <Tabs
        value={tab}
        onChange={(_event, next) => onTabChange(next)}
        aria-label="Patient record sections"
        variant="scrollable"
        scrollButtons="auto"
        sx={clientSx.tabBar}
      >
        {TABS.map((item) => {
          const Icon = item.icon;
          return (
            <Tab
              key={item.id}
              value={item.id}
              id={`patient-tab-${item.id}`}
              aria-controls={`patient-panel-${item.id}`}
              icon={<Icon />}
              iconPosition="start"
              label={item.label}
              sx={{ minHeight: 48, textTransform: 'none' }}
            />
          );
        })}
      </Tabs>

      <TabPanel id="patient-panel-personal" labelledBy="patient-tab-personal" hidden={tab !== 'personal'}>
        <Stack spacing={2}>
          <Section
            title="Personal details"
            description="Name, demographics, and where this patient lives."
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <EntryField
                  label="Name"
                  {...fieldProps('personal', 'name', patient.personal.name)}
                  onChange={(value) => patchPersonal('name', value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ChoiceField
                  label="Sex / Gender"
                  options={SEX_OPTIONS}
                  {...fieldProps('personal', 'sex', patient.personal.sex)}
                  onChange={(value) => patchPersonalChoice('sex', value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <EntryField
                  label="Age"
                  inputMode="numeric"
                  {...fieldProps('personal', 'age', patient.personal.age)}
                  onChange={(value) => patchPersonal('age', sanitizeInteger(value))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <EntryField
                  label="Job"
                  {...fieldProps('personal', 'job', patient.personal.job)}
                  onChange={(value) => patchPersonal('job', value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ChoiceField
                  label="Marital Status"
                  options={MARITAL_STATUS_OPTIONS}
                  {...fieldProps('personal', 'maritalStatus', patient.personal.maritalStatus)}
                  onChange={(value) => patchPersonalChoice('maritalStatus', value)}
                />
              </Grid>
              <Grid item xs={12}>
                <EntryField
                  label="Residence"
                  {...fieldProps('personal', 'residence', patient.personal.residence)}
                  onChange={(value) => patchPersonal('residence', value)}
                />
              </Grid>
            </Grid>
          </Section>
          <Section
            title="3D character"
            description="Choose a metahuman appearance for the simulation scene. Your patient's name and story stay as you entered them."
            sx={{ scrollMarginBottom: 'calc(128px + env(safe-area-inset-bottom, 0px))' }}
          >
            <MetahumanCharacterPicker
              value={patient.personal.characterId}
              error={errors['personal.characterId'] || ''}
              onChange={(characterId) => patchPersonalChoice('characterId', characterId)}
            />
          </Section>
        </Stack>
      </TabPanel>

      <TabPanel id="patient-panel-health" labelledBy="patient-tab-health" hidden={tab !== 'health'}>
        <PatientTemplatePicker
          title="Health profile template"
          description={`Optional. Apply one of your saved health templates or choose an ${ANATOMAGE_PATIENT} to copy fields into this record. Anatomage names and patient-number placeholders are rewritten to he/she based on sex on the Personal tab.`}
          section="health"
          onApply={applyHealthTemplate}
          onError={(message) => setTemplateNotice(message)}
          savePayload={{ health: patient.health }}
        />
        {templateNotice && tab === 'health' ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {templateNotice}
          </Typography>
        ) : null}
        <Section
          title="Health profile"
          description="Complete every field. Fields marked (if applicable) may be left blank."
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <EntryField
                label="Height"
                required
                inputMode="numeric"
                endAdornment="cm"
                {...fieldProps('health', 'heightCm', patient.health.heightCm)}
                onChange={(value) => patchHealth('heightCm', sanitizeInteger(value))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <EntryField
                label="Weight"
                required
                inputMode="decimal"
                endAdornment="kg"
                {...fieldProps('health', 'weightKg', patient.health.weightKg)}
                onChange={(value) => patchHealth('weightKg', sanitizeDecimal(value))}
              />
            </Grid>
            {HEALTH_TEXT_FIELDS.map((field) => (
              <Grid item xs={12} key={field.key}>
                <EntryField
                  label={field.label}
                  multiline
                  required={!HEALTH_OPTIONAL_FIELD_KEYS.has(field.key)}
                  {...fieldProps('health', field.key, patient.health[field.key])}
                  onChange={(value) => patchHealth(field.key, value)}
                />
              </Grid>
            ))}
          </Grid>
        </Section>
      </TabPanel>

      <TabPanel id="patient-panel-pathology" labelledBy="patient-tab-pathology" hidden={tab !== 'pathology'}>
        <Stack spacing={3}>
          <PatientTemplatePicker
            title="Pathology template"
            description={`Optional. Apply one of your saved pathology templates or choose an ${ANATOMAGE_PATIENT} (can differ from the health template) to copy diagnosis, presentation, vitals, and imaging, labs, and exams.`}
            section="pathology"
            onApply={applyPathologyTemplate}
            onError={(message) => setTemplateNotice(message)}
            savePayload={{ pathology: patient.pathology }}
          />
          {templateNotice && tab === 'pathology' ? (
            <Typography variant="body2" color="text.secondary">
              {templateNotice}
            </Typography>
          ) : null}
          <Section title="Pathology" description="Presentation and illness narrative for this patient.">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <EntryField
                  label="Chief Complaint"
                  multiline
                  {...fieldProps('pathology', 'chiefComplaint', patient.pathology.chiefComplaint)}
                  onChange={(value) => patchPathology('chiefComplaint', value)}
                />
              </Grid>
              <Grid item xs={12}>
                <EntryField
                  label="History of Current Illness"
                  multiline
                  minRows={4}
                  {...fieldProps('pathology', 'historyOfCurrentIllness', patient.pathology.historyOfCurrentIllness)}
                  onChange={(value) => patchPathology('historyOfCurrentIllness', value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
                <EntryField
                  label="Stage"
                  multiline
                  minRows={2}
                  {...fieldProps('pathology', 'stage', patient.pathology.stage)}
                  onChange={(value) => patchPathology('stage', value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: 'flex' }}>
                <EntryField
                  label="Complication"
                  multiline
                  minRows={2}
                  {...fieldProps('pathology', 'complication', patient.pathology.complication)}
                  onChange={(value) => patchPathology('complication', value)}
                />
              </Grid>
              <Grid item xs={12}>
                <EntryField
                  label="Symptom"
                  multiline
                  minRows={3}
                  {...fieldProps('pathology', 'symptom', patient.pathology.symptom)}
                  onChange={(value) => patchPathology('symptom', value)}
                />
              </Grid>
            </Grid>
          </Section>

          <Section
            title="Diagnosis / differential"
            description={`Choose the correct diagnoses for this case (Good Choice in simulation).${catalogNote ? ` ${catalogNote}` : ''}`}
          >
            <DataTable
              columns={[
                { key: 'name', label: 'Diagnosis', minWidth: 280 },
              ]}
              minWidth={320}
              rows={patient.pathology.differentials || []}
              emptyLabel="No diagnoses selected yet."
              onRemove={removeDifferentialRow}
              renderCell={(row, column) => {
                const diagnosis = row.name.trim() || 'diagnosis';
                if (column.key === 'name') {
                  return (
                    <CatalogCell
                      id={fieldElementId(patient.id, `differential.${row.id}.name`)}
                      label={`Diagnosis ${diagnosis}`}
                      placeholder="Search diagnoses"
                      options={differentialOptions}
                      name={row.name}
                      objectId={row.objectId}
                      onChange={(name, objectId) => patchDifferentialCommitted(row.id, { name, objectId })}
                    />
                  );
                }
                return null;
              }}
            />
            <Button
              type="button"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addDifferential}
              sx={{ mt: 2, minHeight: 44 }}
            >
              Add diagnosis
            </Button>
          </Section>

          <Section title="Current vital signs" description={VITAL_HINT}>
            <Grid container spacing={2}>
              {VITAL_FIELDS.map((field) => {
                const key = `pathology.vitals.${field.key}`;
                return (
                  <Grid item xs={12} sm={6} md={2.4} key={field.key}>
                    <EntryField
                      id={fieldElementId(patient.id, key)}
                      label={field.label}
                      endAdornment={field.unit}
                      placeholder={field.placeholder}
                      helperText={field.helper}
                      value={patient.pathology.vitals[field.key]}
                      error={errors[key] || ''}
                      onChange={(value) => patchVital(field.key, value)}
                      onBlur={() => onBlurField(key)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Section>

          <Section
            title="Imaging"
            description={(
              <>
                Pick an imaging or bedside study. The file list includes Anatomage imaging and files from{' '}
                <Link component={RouterLink} to="/my-files">My Files</Link>.
                {catalogNote ? ` ${catalogNote}` : ''}
                {fileNote ? ` ${fileNote}` : ''}
              </>
            )}
          >
            <DataTable
              columns={[
                { key: 'name', label: 'Imaging', minWidth: 220 },
                { key: 'result', label: 'Result', minWidth: 180 },
                { key: 'evaluation', label: 'Evaluation', minWidth: 160 },
                { key: 'file', label: 'File', minWidth: 240 },
              ]}
              rows={patient.pathology.imaging}
              emptyLabel="No imaging studies yet."
              onRemove={removeImagingRow}
              expandableResult
              renderCell={(row, column, _index, context) => {
                const study = row.name.trim() || 'study';
                if (column.key === 'name') {
                  return (
                    <CatalogCell
                      id={fieldElementId(patient.id, `imaging.${row.id}.name`)}
                      label={`Imaging for ${study}`}
                      placeholder="Search imaging"
                      options={imagingOptions}
                      name={row.name}
                      objectId={row.objectId}
                      onChange={(name, objectId) => patchImagingCommitted(row.id, { name, objectId })}
                    />
                  );
                }
                if (column.key === 'evaluation') {
                  return (
                    <EvaluationCell
                      id={fieldElementId(patient.id, `imaging.${row.id}.evaluation`)}
                      label={`Evaluation for ${study}`}
                      value={row.evaluation}
                      onChange={(evaluation) => patchImagingCommitted(row.id, { evaluation })}
                    />
                  );
                }
                if (column.key === 'file') {
                  const selected = resolveFileOption(row, fileOptions);
                  const choices = fileOptionsWithSelection(fileOptions, selected);
                  return (
                    <FileTagCell
                      id={fieldElementId(patient.id, `imaging.${row.id}.file`)}
                      label={`File for ${study}`}
                      options={choices}
                      value={selected}
                      onChange={(next) => patchImagingCommitted(row.id, next ? {
                        fileTag: next.tag,
                        fileSource: next.source,
                        fileId: String(next.id),
                      } : { fileTag: '', fileSource: '', fileId: '' })}
                    />
                  );
                }
                if (column.key === 'result') {
                  return (
                    <CellField
                      id={fieldElementId(patient.id, `imaging.${row.id}.result`)}
                      label={`Result for ${study}`}
                      placeholder="Result"
                      value={row.result}
                      expanded={Boolean(context?.expanded)}
                      onChange={(value) => patchImaging(row.id, { result: value })}
                    />
                  );
                }
                return null;
              }}
            />
            <Button
              type="button"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addImaging}
              sx={{ mt: 2, minHeight: 44 }}
            >
              Add imaging study
            </Button>
          </Section>

          <Section
            title="Labs"
            description={`Pick a lab from the investigate list.${catalogNote ? ` ${catalogNote}` : ''}`}
          >
            <DataTable
              columns={[
                { key: 'name', label: 'Lab', minWidth: 240 },
                { key: 'result', label: 'Result', minWidth: 180 },
                { key: 'evaluation', label: 'Evaluation', minWidth: 160 },
              ]}
              minWidth={580}
              rows={patient.pathology.labs}
              emptyLabel="No lab results yet."
              onRemove={removeLabRow}
              expandableResult
              renderCell={(row, column, _index, context) => {
                const lab = row.name.trim() || 'lab result';
                if (column.key === 'name') {
                  return (
                    <CatalogCell
                      id={fieldElementId(patient.id, `lab.${row.id}.name`)}
                      label={`Lab for ${lab}`}
                      placeholder="Search labs"
                      options={labOptions}
                      name={row.name}
                      objectId={row.objectId}
                      onChange={(name, objectId) => patchLabCommitted(row.id, { name, objectId })}
                    />
                  );
                }
                if (column.key === 'evaluation') {
                  return (
                    <EvaluationCell
                      id={fieldElementId(patient.id, `lab.${row.id}.evaluation`)}
                      label={`Evaluation for ${lab}`}
                      value={row.evaluation}
                      onChange={(evaluation) => patchLabCommitted(row.id, { evaluation })}
                    />
                  );
                }
                if (column.key === 'result') {
                  return (
                    <CellField
                      id={fieldElementId(patient.id, `lab.${row.id}.result`)}
                      label={`Result for ${lab}`}
                      placeholder="Result"
                      value={row.result}
                      expanded={Boolean(context?.expanded)}
                      onChange={(value) => patchLab(row.id, { result: value })}
                    />
                  );
                }
                return null;
              }}
            />
            <Button
              type="button"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addLab}
              sx={{ mt: 2, minHeight: 44 }}
            >
              Add lab result
            </Button>
          </Section>

          <Section
            title="Physical Exams"
            description={`Pick a physical exam finding from the exam tab.${catalogNote ? ` ${catalogNote}` : ''}`}
          >
            <DataTable
              columns={[
                { key: 'name', label: 'Exam', minWidth: 240 },
                { key: 'result', label: 'Result', minWidth: 180 },
                { key: 'evaluation', label: 'Evaluation', minWidth: 200 },
              ]}
              minWidth={620}
              rows={patient.pathology.exams || []}
              emptyLabel="No exam findings yet."
              onRemove={removeExamRow}
              expandableResult
              renderCell={(row, column, _index, context) => {
                const exam = row.name.trim() || 'exam finding';
                if (column.key === 'name') {
                  return (
                    <CatalogCell
                      id={fieldElementId(patient.id, `exam.${row.id}.name`)}
                      label={`Exam for ${exam}`}
                      placeholder="Search exams"
                      options={examOptions}
                      name={row.name}
                      objectId={row.objectId}
                      onChange={(name, objectId) => patchExamCommitted(row.id, { name, objectId })}
                    />
                  );
                }
                if (column.key === 'evaluation') {
                  return (
                    <EvaluationCell
                      id={fieldElementId(patient.id, `exam.${row.id}.evaluation`)}
                      label={`Evaluation for ${exam}`}
                      value={row.evaluation}
                      onChange={(evaluation) => patchExamCommitted(row.id, { evaluation })}
                    />
                  );
                }
                if (column.key === 'result') {
                  return (
                    <CellField
                      id={fieldElementId(patient.id, `exam.${row.id}.result`)}
                      label={`Result for ${exam}`}
                      placeholder="Result"
                      value={row.result}
                      expanded={Boolean(context?.expanded)}
                      onChange={(value) => patchExam(row.id, { result: value })}
                    />
                  );
                }
                return null;
              }}
            />
            <Button
              type="button"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addExam}
              sx={{ mt: 2, minHeight: 44 }}
            >
              Add exam finding
            </Button>
          </Section>
        </Stack>
      </TabPanel>
    </Box>
  );
};

export default PatientEditor;
